import type { ProductKey } from '@/router/products'
import type {
  AvailableBundlesParams,
  BundleSubcategory,
  Bundle,
  CheckoutMode,
} from '@/services/subscription'

/**
 * A subscription flow is the product-specific strategy behind the 3 generic
 * steps every sales journey shares:
 *   1. a decision-tree questionnaire → recommended bundles (`available_bundles`);
 *   2. the subscriber's personal + contract details;
 *   3. a summary then Stripe checkout.
 *
 * Everything that differs between products lives in a `SubscriptionFlowDefinition`
 * subclass; the generic engine (services + composables + components) reads from
 * it. Definitions are PURE (no Vue reactivity): they map plain answer objects to
 * plain data, so they stay trivially testable and the reactive wiring stays in
 * the composables.
 */

/** The flow answers decoded from the URL (stepKey → canonical value). */
export type FlowAnswers = Record<string, string>

/** The shared subscription-form inputs a flow reads to derive its period. */
export type FlowPeriodInput = {
  startDate: Date | null
  /** Subscribed duration in months (duration-based flows). */
  duration: number | null
  /** End of stay (date-range flows, e.g. study abroad). */
  stayEnd: Date | null
}

/** A subscription period as the two inclusive dates the API expects (YYYY-MM-DD). */
export type FlowPeriod = { effectiveFrom: string; effectiveUntil: string }

/** The shared subscription-form inputs a flow reads to derive contract metadata. */
export type FlowMetadataInput = {
  /** ISO country code of the destination (date-range flows, e.g. study abroad). */
  destinationCountry: string | null
  /** ISO country code of the subscriber's nationality (arriving-in-France flow). */
  nationality: string | null
}

/** Which optional info-form fields a flow collects from the subscriber. */
export type FlowFieldRequirements = { ssn: boolean; banking: boolean }

/** A standard (non date-prorated) price tag in euros. */
export type PriceTag = { perMonth: number | null; perYear: number | null }

/** Static policy for the generic final-recap screen (`RecapStep`). The reactive
 *  view-model (resolved cards, dates, totals) is assembled by the composable from
 *  these flags; only the product-specific *display rules* live here. */
export type RecapPolicy = {
  /** Show the upfront/monthly payment-mode selector (products allowing monthly). */
  showPaymentSelector: boolean
  /** Show the STUD'LIFE club reassurance block under the recap. */
  showClub: boolean
  /** Contract-dates tacit-renewal row: hidden, or shown as "yes" / "no". */
  tacitRow: 'hidden' | 'yes' | 'no'
  /** Price wording: 'duration' ("X € for N months" / "Y €/mo") or 'stay'
   *  (a precomputed "X € for <stay>" string, for date-range covers). */
  priceMode: 'duration' | 'stay'
}

/** Configuration of an embedded cross-sell: add-ons derived from a base bundle's
 *  `upsell_bundles`, each priced as upsell − base. */
export type CrossSellConfig = {
  /** Answer step holding the user's cross-sell choice (e.g. 'crossSell'). */
  answerKey: string
  /** Whether choosing an add-on bundles a free "exam insurance" line. */
  bundlesExamInsurance: boolean
}

/** Configuration of a separate top-up product fetched from its own subcategory
 *  (e.g. the Tonic-Études surcomplémentaire for the health flow). */
export type TopUpConfig = {
  subcategory: BundleSubcategory
  /** `reference_mysmerra` of the top-up bundle. */
  reference: number
  /** Answer step holding subscribe/skip (e.g. 'topUp'). */
  answerKey: string
  /** Whether the top-up is offered at all for the given answers. */
  isEnabled(answers: FlowAnswers): boolean
}

/* ------------------------------------------------------------------
 * Date helpers shared by every flow's period computation.
 * ------------------------------------------------------------------*/

/** Format a Date as YYYY-MM-DD in local time. Must not go through
 *  `toISOString()`: that converts to UTC and can shift the calendar day. */
export function toIsoDate(d: Date): string {
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

/** Tomorrow at local midnight — the earliest selectable start date, used as a
 *  fallback so a duration-based total can be previewed before a date is picked. */
export function tomorrow(): Date {
  const now = new Date()
  return new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1)
}

/** Inclusive last covered day `months` after `from` (mirrors monthsBetween):
 *  the day before the same-day-of-month anniversary `months` later.
 *
 *  The anniversary's day-of-month is clamped to the target month's last day so a
 *  short month does not overflow into the next one — e.g. 30 June + 8 months →
 *  anniversary 28 Feb (not 2 Mar), last covered day 27 Feb. A naive
 *  `new Date(y, m + months, d - 1)` would roll 29 Feb over to 1 Mar. */
export function addMonthsInclusive(from: Date, months: number): Date {
  const targetMonth = from.getMonth() + months
  const year = from.getFullYear() + Math.floor(targetMonth / 12)
  const month = ((targetMonth % 12) + 12) % 12
  // Day 0 of the next month = last day of the target month.
  const lastDayOfMonth = new Date(year, month + 1, 0).getDate()
  const day = Math.min(from.getDate(), lastDayOfMonth)
  const anniversary = new Date(year, month, day)
  anniversary.setDate(anniversary.getDate() - 1)
  return anniversary
}

/** Normalize a bundle/name segment for matching: lowercase, accents stripped
 *  ("Sécurité" → "securite"). */
function normalizeName(value: string): string {
  return value.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().trim()
}

/** The add-on segment of an upsell name (e.g. "minimale+sécurité" → "sécurité").
 *  Upsell names follow "<base>+<addon>", so the add-on is the last segment. */
function addonSegment(upsell: Bundle): string {
  return upsell.name.split('+').pop() ?? ''
}

export abstract class SubscriptionFlowDefinition {
  /** The product this flow drives (selects the decision tree + slugs). */
  abstract readonly product: ProductKey
  /** The backend bundle subcategory queried by `available_bundles`. */
  abstract readonly subcategory: BundleSubcategory
  /** Map of plan slug → bundle `reference_mysmerra` (results ordering + lookup). */
  abstract readonly planReference: Record<string, number>

  /** Build the `available_bundles` params from the answers, or null until the
   *  inputs the decision tree needs are all answered. */
  abstract bundlesParams(answers: FlowAnswers): AvailableBundlesParams | null

  /** Embedded cross-sell config, or null when the flow has none. */
  readonly crossSell: CrossSellConfig | null = null
  /** Separate top-up config, or null when the flow has none. */
  readonly topUp: TopUpConfig | null = null
  /** Optional info-form fields. Default: neither SSN nor banking details. */
  readonly fields: FlowFieldRequirements = { ssn: false, banking: false }
  /** Whether the contract renews tacitly at the end of its period. Default:
   *  true (yearly-renewing covers). Fixed-period flows (e.g. study abroad,
   *  whose cover is the stay itself) override to false. */
  readonly tacitRenewal: boolean = true

  /** Whether a member already holding an active contract for this product may take out
   *  another one. Default: false — a yearly cover renews, it is not bought twice, and the
   *  connected space says so by turning the product's card into a management card.
   *
   *  A stay-based cover is a different object: it insures one trip, so a second trip means a
   *  second contract, and a member extending their stay needs one while the first is still
   *  running (SI-1477). Declared here rather than as a product list in the view, for the same
   *  reason the third-party-payment card is read off `fields.banking`: the flow already
   *  describes the product, and a list elsewhere would be one more thing to keep in step. */
  readonly allowsRepeatSubscription: boolean = false

  /** Whether the results screen shows only the bundles the backend flags as
   *  `recommended`, hiding the rest. Default: false (show every mapped bundle).
   *  Flows whose decision tree already singles out one pack (e.g. study abroad,
   *  Europe vs World) override to true. */
  readonly recommendedOnly: boolean = false

  /** Final-recap display policy. Default: a duration-based, tacitly-renewing cover
   *  paid upfront or monthly, with the club block hidden. Flows override per their
   *  payment cadence, renewal and pricing model. */
  readonly recap: RecapPolicy = {
    showPaymentSelector: true,
    showClub: false,
    tacitRow: 'yes',
    priceMode: 'duration',
  }

  /** The per-card display price of a bundle (monthly / yearly), in euros.
   *  Default: the standard monthly_price / annual_price. Flows whose bundles
   *  price differently (e.g. travel packs using the international rate) override. */
  displayPrice(bundle: Bundle): PriceTag {
    return { perMonth: bundle.monthlyPrice, perYear: bundle.annualPrice }
  }

  /** Reverse of `planReference`: a bundle reference → plan slug, or null. */
  planKeyOf(reference: number): string | null {
    for (const [key, ref] of Object.entries(this.planReference)) {
      if (ref === reference) return key
    }
    return null
  }

  /** Map an upsell bundle to the cross-sell value it represents, or null when
   *  unrecognized. Default: parse the "<base>+<addon>" name and recognize the
   *  shared AVE add-ons (security / indispensable). */
  crossSellValueOf(upsell: Bundle): string | null {
    const segment = normalizeName(addonSegment(upsell))
    if (segment.includes('indispensable')) return 'indispensable'
    if (segment.includes('securite')) return 'security'
    return null
  }

  /** i18n key (in the `bundles` namespace) for an upsell add-on's label, derived
   *  the way the backend slugifies names ("Sécurité" → "securite"). */
  crossSellLabelKey(upsell: Bundle): string {
    return normalizeName(addonSegment(upsell)).replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '')
  }

  /** Display order of a cross-sell value (lower comes first). */
  crossSellOrder(value: string): number {
    const order: Record<string, number> = { security: 0, indispensable: 1 }
    return order[value] ?? 99
  }

  /** Whether the top-up is currently subscribed, per the answers. */
  isTopUpSubscribed(answers: FlowAnswers): boolean {
    if (!this.topUp) return false
    return answers[this.topUp.answerKey] === 'subscribe'
  }

  /** Contract subscriber profile from the answers. Default: child → parent. */
  subscriberProfile(answers: FlowAnswers): 'student' | 'parent' {
    return answers.forWhom === 'child' ? 'parent' : 'student'
  }

  /** The subscription period from the shared form inputs, or null when not
   *  determined yet. Default: start date (or tomorrow) over `duration` months. */
  period(input: FlowPeriodInput): FlowPeriod | null {
    const months = input.duration
    if (!months) return null
    const from = input.startDate ?? tomorrow()
    const until = addMonthsInclusive(from, months)
    return { effectiveFrom: toIsoDate(from), effectiveUntil: toIsoDate(until) }
  }

  /** Product-specific metadata attached to every contract line of the draft, or
   *  null when the flow carries none. Reads the answers plus the shared form
   *  inputs (e.g. study abroad sends the destination country). Default: none. */
  contractMetadata(_answers: FlowAnswers, _input: FlowMetadataInput): Record<string, string> | null {
    return null
  }

  /** Stripe checkout mode from the chosen payment cadence. */
  checkoutMode(paymentMode: 'upfront' | 'monthly'): CheckoutMode {
    return paymentMode === 'upfront' ? 'one_time_payment' : 'subscription'
  }
}
